import { AlertCircle } from '@tamagui/lucide-icons';
import { BaseStationQRCodeContent } from 'common';
import { Base64 } from 'js-base64';
import pako from 'pako';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import { Spinner, YStack } from 'tamagui';

import { useAppSync } from '../../contexts/AppSyncContext';
import { useFirmwareContext } from '../../contexts/FirmwareContext';

interface BaseStationQRCodeProps {
  size?: number;
}

export const BaseStationQRCode: React.FC<BaseStationQRCodeProps> = ({ size = 280 }) => {
  const {
    iotStatus,
    iotStatusLoading,
    baseStationConfig,
    baseStationConfigLoading,
    hardwareVersion,
    hardwareVersionLoading,
  } = useFirmwareContext();

  const { sensorsLoading } = useAppSync();

  if (sensorsLoading) {
    return (
      <YStack height={size} alignItems="center" justifyContent="center">
        <Spinner size="large" />
      </YStack>
    );
  }

  const getQRCodeValue = () => {
    if (!iotStatus?.hub_id) return null;

    const hubInfo = {
      hubId: iotStatus.hub_id,
      dsk: baseStationConfig?.serial_number || 'unknown',
      type: 'Base Station',
      version: hardwareVersion?.version || 'unknown',
    } satisfies BaseStationQRCodeContent;

    const jsonString = JSON.stringify(hubInfo);
    const compressedString = pako.deflate(jsonString);
    const base64Info = Base64.fromUint8Array(compressedString, true);

    // TODO: use proper env variable when available
    // Production: https://api.hghomeguardian.com/basestation/[[Encoded Info]]
    // Develop: http://api.develop.heartbeat.homes/basestation/[[Encoded Info]]
    const apiGateway = 'api.develop.heartbeat.homes';

    return `https://${apiGateway}/basestation/${base64Info}`;
  };

  if (iotStatusLoading || hardwareVersionLoading || baseStationConfigLoading) {
    return (
      <YStack height={size} alignItems="center" justifyContent="center">
        <Spinner size="large" />
      </YStack>
    );
  }

  const qrValue = getQRCodeValue();

  if (!qrValue) {
    return (
      <YStack height={size} justifyContent="center">
        <AlertCircle size={size * 0.5} color="$red10" />
      </YStack>
    );
  }

  return <QRCodeSVG value={qrValue} size={size} marginSize={1} level="M" minVersion={4} />;
};
